import { useEffect, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Check, Loader2, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { CityOption, cityLabel, searchCitiesRemote, POPULAR_CITIES, formatPostal, validatePostal } from "@/data/cities";

interface CityComboboxProps {
  label: string;
  placeholder?: string;
  value: CityOption | null;
  onChange: (value: CityOption | null) => void;
  error?: string | undefined;
  autoComplete?: string;
  zip?: string;
  onZipChange?: (zip: string) => void;
}

export const CityCombobox = ({ label, placeholder, value, onChange, error, autoComplete, zip, onZipChange }: CityComboboxProps) => {
  const id = useId();
  const zipId = `${id}-zip`;
  const listboxId = `${id}-listbox`;
  const [query, setQuery] = useState(value ? cityLabel(value) : "");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [results, setResults] = useState<CityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [zipOpen, setZipOpen] = useState(!!zip);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(value ? cityLabel(value) : "");
  }, [value]);

  // Debounced remote search; instant filter on the popular-markets list while typing.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      abortRef.current?.abort();
      return;
    }
    // Instant local fallback while the remote request is in flight.
    const lower = q.toLowerCase();
    const local = POPULAR_CITIES.filter((c) => cityLabel(c).toLowerCase().includes(lower)).slice(0, 8);
    setResults(local);
    setLoading(true);

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const handle = window.setTimeout(async () => {
      try {
        const remote = await searchCitiesRemote(q, ctrl.signal, 8);
        if (!ctrl.signal.aborted) setResults(remote.length > 0 ? remote : local);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          // Keep the local fallback list on network failure.
          console.warn("city search failed", err);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 220);
    return () => {
      window.clearTimeout(handle);
      ctrl.abort();
    };
  }, [query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (c: CityOption) => {
    onChange(c);
    setQuery(cityLabel(c));
    setOpen(false);
    // If the picked result came from a ZIP search, auto-fill the ZIP field.
    if (c.zip && onZipChange) {
      onZipChange(formatPostal(c.zip, c.country));
      setZipOpen(true);
    }
  };

  const handleChange = (raw: string) => {
    setQuery(raw);
    setActiveIdx(0);
    setOpen(true);
    if (value && raw !== cityLabel(value)) onChange(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && results[activeIdx]) {
        e.preventDefault();
        handleSelect(results[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const selected = !!value;
  const zipValid = validatePostal(zip ?? "", value?.country);
  const zipPlaceholder = value?.country === "CA" ? "A1A 1A1" : "48243";
  const zipMaxLen = value?.country === "CA" ? 7 : 5;

  return (
    <div ref={wrapperRef} className="relative">
      <Label htmlFor={id}>{label} *</Label>
      <div className="relative mt-1">
        <MapPin className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none", selected ? "text-electric" : "text-muted-foreground")} />
        <Input
          id={id}
          autoComplete={autoComplete ?? "off"}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => query.length >= 1 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? "City or ZIP"}
          aria-invalid={!!error}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={open && results[activeIdx] ? `${id}-option-${activeIdx}` : undefined}
          role="combobox"
          className={cn("pl-9", selected && "border-electric/40")}
        />
        {selected && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-electric" />}
        {!selected && loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {results.map((c, idx) => (
            <li
              id={`${id}-option-${idx}`}
              key={`${c.city}-${c.state}-${c.country}`}
              role="option"
              aria-selected={idx === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
              onMouseEnter={() => setActiveIdx(idx)}
              className={cn(
                "flex items-center justify-between px-3 py-2 cursor-pointer text-sm",
                idx === activeIdx ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
              )}
            >
              <span className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-medium">{c.city}</span>
                <span className="text-muted-foreground">{c.state}</span>
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.country}</span>
            </li>
          ))}
        </ul>
      )}
      {open && query.trim().length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-lg p-3 text-sm text-muted-foreground">
          No cities match "{query}". Try a different spelling.
        </div>
      )}
      {error && <p className="mt-1 text-xs font-medium text-destructive">{error}</p>}

      {/* Optional ZIP / postal reveal — only available once a city is picked. */}
      {selected && onZipChange && (
        <div className="mt-1.5">
          {!zipOpen ? (
            <button
              type="button"
              onClick={() => { setZipOpen(true); requestAnimationFrame(() => zipInputRef.current?.focus()); }}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-electric transition-colors"
            >
              <Plus className="w-3 h-3" /> Add ZIP for a sharper quote
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <Label htmlFor={zipId} className="sr-only">ZIP / postal code</Label>
              <Input
                id={zipId}
                ref={zipInputRef}
                value={zip ?? ""}
                onChange={(e) => onZipChange(formatPostal(e.target.value.slice(0, zipMaxLen), value?.country))}
                placeholder={zipPlaceholder}
                inputMode={value?.country === "CA" ? "text" : "numeric"}
                maxLength={value?.country === "CA" ? 7 : 5}
                aria-invalid={!zipValid}
                className={cn("h-8 w-32 text-sm", !zipValid && "border-destructive")}
              />
              <span className="text-[11px] text-muted-foreground">Optional · sharper rate</span>
              <button
                type="button"
                onClick={() => { onZipChange(""); setZipOpen(false); }}
                className="ml-auto text-muted-foreground hover:text-foreground"
                aria-label="Remove ZIP"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {zipOpen && !zipValid && zip && (
            <p className="mt-1 text-[11px] font-medium text-destructive">
              {value?.country === "CA" ? "Use the format A1A 1A1." : "Enter a 5-digit ZIP code."}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CityCombobox;